import {
  Badge,
  BodyText,
  Box,
  Card,
  DetailText,
  Divider,
  Flex,
  Heading,
  InlineLink,
} from '@utilitywarehouse/hearth-react';
import SpotConfettiLight from '@utilitywarehouse/hearth-svg-assets/lib/spot-confetti-light.svg';
import SpotPiggyBankLight from '@utilitywarehouse/hearth-svg-assets/lib/spot-piggy-bank-light.svg';
import clsx from 'clsx';

import { EXTERNAL_LINKS } from '@constants/externalLinks';
import { ProductType } from '@generated/gql/graphql';

import { ClaimStep } from './ClaimStep';
import { TierRow } from './TierRow';
import './styles.scss';
import {
  NO_BONUS,
  getBonusAmount,
  getTierServices,
  getVisibleTiers,
  hasCashbackCard,
} from './tiers';

interface WelcomeBonusSectionProps {
  selectedServices: ProductType[];
  insuranceAvailable: boolean;
}

const WelcomeBonusSection = ({
  selectedServices,
  insuranceAvailable,
}: WelcomeBonusSectionProps) => {
  const tierServices = getTierServices(selectedServices);
  const visibleTiers = getVisibleTiers(insuranceAvailable);
  const lastVisibleTier = visibleTiers[visibleTiers.length - 1];
  const maxBonus = lastVisibleTier.amount;
  const bonusAmount = getBonusAmount(tierServices.length, visibleTiers);
  const isMaxed = tierServices.length >= lastVisibleTier.count;
  const hasServices = tierServices.length > 0;

  return (
    <Card
      className="welcomeBonus"
      variant="subtle"
      colorScheme="neutralSubtle"
      paddingNone
      width="100%"
      direction="column"
      overflow="clip"
    >
      <Flex
        direction={{ mobile: 'column', tablet: 'row' }}
        alignItems="stretch"
        width="100%"
      >
        <Flex
          direction={{ mobile: 'row', tablet: 'column' }}
          gap="250"
          alignItems="center"
          justifyContent={{ mobile: 'start', tablet: 'center' }}
          padding="200"
          width={{ mobile: '100%', tablet: '40%' }}
          minHeight={{ mobile: 'auto', tablet: '300px' }}
          textAlign={{ mobile: 'left', tablet: 'center' }}
          flexShrink="0"
        >
          <img
            className="welcomeBonus__illustration"
            src={isMaxed ? SpotConfettiLight : SpotPiggyBankLight}
            alt={isMaxed ? 'Party popper' : 'Wallet with coins'}
          />
          <Flex
            direction="column"
            spacing="sm"
            alignItems={{ mobile: 'start', tablet: 'center' }}
          >
            <Heading
              as="h3"
              size="sm"
              textAlign={{ mobile: 'left', tablet: 'center' }}
            >
              {isMaxed
                ? 'Maximum welcome cashback card bonus unlocked!'
                : `Get up to ${maxBonus} to spend on your shopping`}
            </Heading>
            <BodyText
              size="md"
              color="secondary"
              textAlign={{ mobile: 'left', tablet: 'center' }}
            >
              {isMaxed
                ? `Enjoy every penny - your ${maxBonus} bonus is all yours!`
                : 'Each service you add increases your cashback welcome bonus!'}
            </BodyText>
          </Flex>
        </Flex>

        <Flex display={{ mobile: 'none', tablet: 'flex' }}>
          <Divider orientation="vertical" decorative />
        </Flex>
        <Box display={{ mobile: 'block', tablet: 'none' }}>
          <Divider orientation="horizontal" decorative />
        </Box>

        <Flex
          direction="column"
          gap="100"
          alignItems="start"
          paddingX={{ mobile: '200', tablet: '250' }}
          paddingY="200"
          flex="1"
          minWidth="0"
        >
          <Flex justifyContent="between" alignItems="center" width="100%">
            <Heading as="h3" size="md">
              Your welcome bonus
            </Heading>
            <DetailText
              size="2xl"
              className={clsx({
                welcomeBonus__mutedAmount: bonusAmount === NO_BONUS,
              })}
            >
              {bonusAmount}
            </DetailText>
          </Flex>

          {bonusAmount === NO_BONUS && (
            <Flex width="auto" paddingBottom="100">
              <Badge colorScheme="functional" variant="outline" size="md">
                {hasServices
                  ? 'Add another service to unlock your bonus'
                  : 'Add services to unlock your bonus'}
              </Badge>
            </Flex>
          )}

          <Flex direction="column" gap="100" width="100%">
            {visibleTiers.map((tier) => (
              <TierRow
                key={tier.count}
                tier={tier}
                tierServices={tierServices}
              />
            ))}
          </Flex>

          {bonusAmount !== NO_BONUS && (
            <ClaimStep cashbackCardAdded={hasCashbackCard(selectedServices)} />
          )}

          <Flex paddingTop="100" maxWidth={{ mobile: '100%', tablet: '375px' }}>
            <BodyText size="sm" color="secondary">
              Paid in £50 monthly increments as you add services (2 = £50, 3 =
              £150{insuranceAvailable && ', 4+ = £250'}). See{' '}
              <InlineLink
                href={EXTERNAL_LINKS.termsCbcSection}
                target="_blank"
                rel="noreferrer noopener"
                hideOpenIcon
              >
                Terms
              </InlineLink>{' '}
              and{' '}
              <InlineLink
                href={EXTERNAL_LINKS.faqsCbcSection}
                target="_blank"
                rel="noreferrer noopener"
                hideOpenIcon
              >
                FAQs
              </InlineLink>
              .
            </BodyText>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};

export { WelcomeBonusSection };
